<template>
    <div class="choropleth-container" ref="choroplethMap">
        <ChoroplethLegend
            v-if="google && map"
            :palette="palette"
            :minValue="minValue"
            :maxValue="maxValue"
            :map="map"
            :google="google"
        />
    </div>
</template>

<script>
// import loadGoogleCharts from './GoogleChartLoader';
import { mapsApiKey, firebaseConfig } from "@/firebase.js";
import { Loader } from "@googlemaps/js-api-loader";
import { mapSettings } from "@/constants/mapSettings";
import ChoroplethLegend from "./ChoroplethLegend.vue";
import { getIndicatorKeys, getIndicatorNames } from "./StrataDashboardHelperFunctions";

export default {
    name: "ChoroplethGeoChart",
    components: {
        ChoroplethLegend,
    },
    data: () => ({
        google: null,
        map: null,
        mapCenter: {
            lat: 5.152149,
            lng: 46.19961599999999,
        },
        choroplethProperties: [],
        palette: [
            "#8acb9d",
            "#c5df74",
            "#fff34b",
            "#fbd900",
            "#fdb417",
            "#f5961d",
            "#ec7822",
            "#ed4c34",
            "#ee2045",
            "#d81d3f",
        ],
        opacityCode: "E6", // E6 = 90%
        eventListeners: [],
        minValue: null,
        maxValue: null,
        options: {
            displayRegionName: true,
        },
    }),
    props: {
        data: {
            type: Object,
            required: true,
        },
        bbox: {
            type: Object,
            required: true,
        },
        selectedIndicatorKey: {
            type: String,
            required: false,
            default: "region_value",
        },
        selectedIndicatorName: {
            type: String,
            required: false,
            default: "STRATA SCORE",
        },
        isTable: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    mounted() {
        const loader = new Loader({
            apiKey: this.apiKey,
            libraries: ["geometry", "drawing"],
            version: "quarterly",
        });
        loader.load().then((google) => {
            this.google = google;
            const mapContainer = this.$refs.choroplethMap;
            this.map = new this.google.maps.Map(mapContainer, this.mapConfig);
            this.setBbox(this.bbox);
            this.createChoropleth(
                this.selectedIndicatorKey,
                this.selectedIndicatorName
            );
        });
    },
    methods: {
        setBbox(bbox) {
            if (bbox !== null) {
                var bounds = new this.google.maps.LatLngBounds(
                    new this.google.maps.LatLng(bbox.SW[1], bbox.SW[0]),
                    new this.google.maps.LatLng(bbox.NE[1], bbox.NE[0])
                );

                this.map.fitBounds(bounds, 0);
            }
        },
        createChoropleth(indicatorKey, indicatorName) {
            // load geojson
            this.loadData(this.data, indicatorKey);
            this.generateColours(this.choroplethProperties, this.palette);

            // set map style
            this.map.data.setStyle((feature) => {
                let regionName = feature.getProperty("region_name");
                let choroplethObject = this.choroplethProperties.find((obj) => {
                    return obj.regionName === regionName;
                });

                let regionColour = choroplethObject.colour;
                return {
                    fillColor: regionColour, // the polygon fill
                    strokeWeight: 0.8, // width of the border line
                    strokeColor: "#ffffff", // color of the HR border
                    fillOpacity: 1, // how opaque is the polygon fill? 1 means totally opaque.
                    strokeOpacity: 1, // how opaque is the border line
                    zIndex: 0,
                };
            });

            // set mouse listerners

            // a popup with the Region name and the index score
            var infoWindow = new this.google.maps.InfoWindow({
                zIndex: 2,
            });

            if(this.isTable) {
                let indicatorKeyNameMap = {}
                let keys = getIndicatorKeys(this.data.datasets[0].data);
                let names = getIndicatorNames(keys);

                keys.forEach((key, index) => {
                    indicatorKeyNameMap[key] = names[index];
                });
                indicatorKeyNameMap["region_value"] = "STRATA SCORE";

                this.indicatorKeyNameMap = indicatorKeyNameMap;
            }

            this.eventListeners.push(
                this.map.data.addListener("mouseover", (event) => {
                    // adds black outline on mouseover
                    this.map.data.revertStyle();
                    this.map.data.overrideStyle(event.feature, {
                        strokeWeight: 2,
                        strokeColor: "black",
                        zIndex: 1,
                    });
                    if(this.isTable) {
                        this.showTableForWindow(event, infoWindow, this.indicatorKeyNameMap);
                        return;
                    }
                    this.showIndicatorForWindow(event, infoWindow, indicatorKey, indicatorName)
                })
            );

            this.eventListeners.push(
                this.map.data.addListener("mouseout", function () {
                    this.map.data.revertStyle();
                    infoWindow.close();
                })
            );
        },
        showTableForWindow(event, infoWindow, indicatorKeyNameMap) {
            let values = [];
            let regionName = event.feature.getProperty("ADM0_NAME");
            let indicatorKeys = Object.keys(indicatorKeyNameMap);
            console.log("indicator keys", indicatorKeys);
            indicatorKeys.forEach((key) => {
                let value = event.feature.getProperty(key);
                if(value) {
                    values.push({
                        key: indicatorKeyNameMap[key],
                        value: value.toFixed(2)
                    });
                }
            })

            infoWindow.setPosition(event.latLng);
            infoWindow.setOptions({
                pixelOffset: { width: 0, height: -3 },
            });

            let infoWindowContent = `
                <div class="info-window-content">
                    <h3>${regionName}</h3>
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th>Indicator</th>
                                <th>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${values.map((value) => {
                                return `
                                    <tr>
                                        <td>${value.key}</td>
                                        <td>${value.value}</td>
                                    </tr>
                                `;
                            }).join("")}
                        </tbody>
                    </table>
                </div>
            `

            infoWindow.setContent(infoWindowContent);
            infoWindow.open(this.map);
        },
        showIndicatorForWindow(event, infoWindow, indicatorKey, indicatorName) {
            var regionName = event.feature.getProperty("region_name");
            var regionValue =
                event.feature.getProperty(indicatorKey) === null
                    ? "(data missing)"
                    : event.feature.getProperty(indicatorKey);

            infoWindow.setPosition(event.latLng);
            infoWindow.setOptions({
                pixelOffset: { width: 0, height: -3 },
            });

            let infoWindowContent = `
Region: ${regionName}
<br>
${indicatorName}: ${regionValue.toFixed(2)}           
`;

            if (!this.options.displayRegionName) {
                infoWindowContent = `
${indicatorName}: ${regionValue.toFixed(2)}
`;
            }

            infoWindow.setContent(infoWindowContent);

            infoWindow.open(this.map);
        },
        loadData(data, indicatorKey) {
            let dataset = data.datasets[0];
            if (dataset.options) {
                this.options = dataset.options;
            }
            let features = dataset.data.features;
            features.forEach((feature) => {
                this.choroplethProperties.push({
                    regionValue: feature.properties[indicatorKey],
                    regionName: feature.properties.region_name,
                    colour: null,
                });
                this.map.data.addGeoJson(feature);
            });
        },
        clearMap() {
            this.map.data.forEach((feature) => {
                this.map.data.remove(feature);
            });
            this.eventListeners.forEach((event) => {
                event.remove();
            });
            this.choroplethProperties = [];
        },
        generateColours(choroplethProperties, palette) {
            // normalise values to be between zero and 1
            // if value less than increment then assign that colour to it

            // finds the min and max of some key in an object array
            const minmax = (someArrayOfObjects, someKey) => {
                const values = someArrayOfObjects.map(
                    (value) => value[someKey]
                );
                return {
                    min: Math.min.apply(null, values),
                    max: Math.max.apply(null, values),
                };
            };
            let minMaxValues = minmax(choroplethProperties, "regionValue");
            let min = minMaxValues.min;
            let max = minMaxValues.max;

            this.maxValue = max;
            this.minValue = min;

            // generates normalised value between 0 and 1 with 1 being max of dataset and 0 being minimum
            const getNormalisedValue = (value, min, max) => {
                return (value - min) / (max - min);
            };

            choroplethProperties.forEach((region) => {
                let normalValue = getNormalisedValue(
                    region.regionValue,
                    min,
                    max
                );

                // in specific cases e.g. min === max, the normal value will be NaN due to dividing by zero
                // in such cases we should return the first palette index as a fall back option
                if (!isNaN(normalValue)) {
                    // gets length of palette and floors to index value
                    let paletteIndex = Math.floor(
                        normalValue * (palette.length - 1)
                    );
                    // sets colour
                    region.colour = palette[paletteIndex];
                } else {
                    region.colour = palette[0];
                }

                // add opacity code to end of hex colour string
                region.colour += this.opacityCode;
            });
        },
    },
    computed: {
        apiKey() {
            return mapsApiKey || firebaseConfig.apiKey;
        },
        mapConfig() {
            return {
                ...mapSettings,
                center: this.mapCenter,
                disableDefaultUI: true,
            };
        },
    },
    watch: {
        selectedIndicatorKey(newIndicator, oldIndicator) {
            if (newIndicator !== oldIndicator) {
                this.clearMap();
                this.createChoropleth(newIndicator, this.selectedIndicatorName);
            }
        },
    },
};
</script>
<style scoped>
.choropleth-container {
    min-height: 600px;
    width: 100%;
}
</style>